'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetTrades } from '@morfi/backend-sdk-v2/client';
import { type Context, createContext, type ReactNode, use, useEffect, useState, useSyncExternalStore } from 'react';
import { MARKET_TRADES_TAPE_FIELDS } from '@/lib/sdk/projections';
import { marketTradeLiveFrom } from '@/lib/sdk/trade-scan-boundary';
import { createMarketFeedTradeStore, type MarketFeedTradeSnapshot, type MarketFeedTradeStore, selectLiveMarketTradeEvents } from './market-feed-trade-store';
const MarketFeedTradeStoreContext: Context<MarketFeedTradeStore | null> = createContext<MarketFeedTradeStore | null>(null);
export interface MarketFeedTradeProviderProps {
  readonly marketIds: readonly string[];
  readonly children: ReactNode;
}

/**
 * Owns one SDK trade query for the complete feed and fans its bounded live
 * projection into per-market selector channels. The context value never
 * changes; high-frequency updates bypass Context and notify one market only.
 *
 * The query follows each market's bonding-curve trade subject only. A
 * graduated market's winner keeps trading on its own AMM pool, but those swaps
 * belong to the winners surfaces: a resolved card's bars are frozen at the
 * conviction at resolution, so routing winner swaps onto it would animate a
 * market that can no longer move.
 */
export function MarketFeedTradeProvider(t0) {
  const $ = _c(17);
  const {
    marketIds,
    children
  } = t0;
  const [store] = useState(_temp);
  const [from] = useState(_temp2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...MARKET_TRADES_TAPE_FIELDS, "market"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== from || $[2] !== marketIds) {
    t2 = {
      markets: marketIds,
      from,
      fields: t1
    };
    $[1] = from;
    $[2] = marketIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const query = useGetTrades(t2);
  let t3;
  let t4;
  if ($[4] !== marketIds || $[5] !== store) {
    t3 = () => {
      store.retainMarkets(marketIds);
    };
    t4 = [marketIds, store];
    $[4] = marketIds;
    $[5] = store;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== marketIds || $[9] !== query.liveSinceMountIds || $[10] !== query.rows || $[11] !== store) {
    t5 = () => {
      store.ingest(selectLiveMarketTradeEvents(query.rows, query.liveSinceMountIds, marketIds));
    };
    t6 = [marketIds, query.liveSinceMountIds, query.rows, store];
    $[8] = marketIds;
    $[9] = query.liveSinceMountIds;
    $[10] = query.rows;
    $[11] = store;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== children || $[15] !== store) {
    t7 = <MarketFeedTradeStoreContext.Provider value={store}>{children}</MarketFeedTradeStoreContext.Provider>;
    $[14] = children;
    $[15] = store;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/** Subscribe to the bounded live overlay projection for one market only. */
function _temp2() {
  return marketTradeLiveFrom();
}
function _temp() {
  return createMarketFeedTradeStore();
}
export function useMarketFeedTrades(market) {
  const $ = _c(5);
  const store = use(MarketFeedTradeStoreContext);
  if (store === null) {
    throw new Error("useMarketFeedTrades must be used within MarketFeedTradeProvider");
  }
  let t0;
  let t1;
  let t2;
  if ($[0] !== market || $[1] !== store) {
    t0 = listener => store.subscribe(market, listener);
    t1 = () => store.getSnapshot(market);
    t2 = () => store.getSnapshot(market);
    $[0] = market;
    $[1] = store;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  return useSyncExternalStore(t0, t1, t2);
}